import { estimateMasses, waistToHeightRatio } from '~/calculations/bodyComposition'
import { summariseSite } from '~/calculations/measurementQuality'
import { estimateNavyBodyFat, hasEstimateValue } from '~/calculations/navyCircumference'
import type {
  DerivedMeasurementSnapshot,
  MeasurementSessionRecord,
  StoredSiteReading,
} from '~/models/measurementSession'
import { MEASUREMENT_ALGORITHM_VERSIONS } from '~/models/measurementSession'
import {
  type MeasurementSiteKey,
  NAVY_FORMULA_SITES,
  REQUIRED_SITES,
  SITE_LABELS,
} from '~/models/measurementSite'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import { putMeasurementSession } from '~/storage/repositories/measurementRepository'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getProfile } from '~/storage/repositories/profileRepository'
import { getSettings } from '~/storage/repositories/settingsRepository'
import { getWeightEntry } from '~/storage/repositories/weightRepository'
import { type CalendarDate, toIsoTimestamp } from '~/types/dates'
import { newId } from '~/types/ids'
import type { Centimetres, Kilograms } from '~/types/units'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'

/**
 * Weekly measurement capture.
 *
 * Orchestration only: every calculation it uses is pure and lives under
 * `calculations/`. This layer reads what the formula needs, calls those
 * functions, and persists the result with its algorithm versions.
 */

/** Readings entered for one site. A site is either skipped or fully recorded. */
export type SiteInput = readonly number[]

export interface MeasurementInput {
  readonly date: CalendarDate
  readonly sites: Readonly<Partial<Record<MeasurementSiteKey, SiteInput>>>
  readonly conditionNote?: string
}

export interface MeasurementIssue {
  readonly site: MeasurementSiteKey | 'session'
  readonly message: string
}

/**
 * Validate and summarise every recorded site.
 *
 * A site present in the input must carry exactly three valid readings; a site
 * the user skipped is simply absent (D-045).
 */
export function summariseSites(
  sites: MeasurementInput['sites'],
  thresholdCm: Centimetres,
): Result<
  Readonly<Partial<Record<MeasurementSiteKey, StoredSiteReading>>>,
  readonly MeasurementIssue[]
> {
  const issues: MeasurementIssue[] = []
  const summarised: Partial<Record<MeasurementSiteKey, StoredSiteReading>> = {}

  for (const site of REQUIRED_SITES) {
    if (sites[site] === undefined) {
      issues.push({ site, message: `${SITE_LABELS[site]} is required.` })
    }
  }

  for (const [key, readings] of Object.entries(sites) as [MeasurementSiteKey, SiteInput][]) {
    const result = summariseSite(readings, thresholdCm)
    if (result.status !== 'ok') {
      issues.push({ site: key, message: issueMessage(key, result.issue) })
      continue
    }
    summarised[key] = {
      readings: [...result.quality.readings],
      medianCm: result.quality.medianCm,
      spreadCm: result.quality.spreadCm,
      exceedsThreshold: result.quality.exceedsThreshold,
      thresholdUsedCm: result.quality.thresholdUsedCm,
    }
  }

  return issues.length > 0 ? err(issues) : ok(summarised)
}

function issueMessage(site: MeasurementSiteKey, issue: string): string {
  const label = SITE_LABELS[site]
  if (issue === 'wrongReadingCount') {
    return `${label} needs exactly three readings. Leave it blank to skip it this week.`
  }
  if (issue === 'nonPositiveReading') return `${label} readings must be greater than zero.`
  return `${label} readings must be numbers, for example 88.4.`
}

/**
 * Derive the estimate and its dependants from the saved medians.
 *
 * The median is what feeds the formula — not the mean, and not a single
 * reading. A missing weight suppresses the mass estimates only; the body-fat
 * percentage and waist-to-height ratio do not depend on it.
 */
export function deriveSnapshot(
  sites: Readonly<Partial<Record<MeasurementSiteKey, StoredSiteReading>>>,
  heightCm: Centimetres,
  weightKg: Kilograms | undefined,
): DerivedMeasurementSnapshot {
  const waistMedian = sites[NAVY_FORMULA_SITES.waist]?.medianCm
  const neckMedian = sites[NAVY_FORMULA_SITES.neck]?.medianCm

  const estimate = estimateNavyBodyFat({
    heightCm,
    waistAtNavelCm: waistMedian === undefined ? undefined : (waistMedian as Centimetres),
    neckCm: neckMedian === undefined ? undefined : (neckMedian as Centimetres),
  })

  const ratio =
    waistMedian === undefined ? undefined : waistToHeightRatio(waistMedian as Centimetres, heightCm)

  const masses =
    hasEstimateValue(estimate) && weightKg !== undefined
      ? estimateMasses(weightKg, estimate.bodyFatPercent)
      : undefined

  return {
    bodyFatStatus: estimate.status,
    ...(hasEstimateValue(estimate) ? { bodyFatPercent: estimate.bodyFatPercent as number } : {}),
    ...(estimate.status === 'invalid' ? { bodyFatInvalidReason: estimate.reason } : {}),
    ...(ratio === undefined ? {} : { waistToHeightRatio: ratio }),
    ...(masses === undefined
      ? {}
      : { fatMassKg: masses.fatMassKg as number, leanMassKg: masses.leanMassKg as number }),
    sourceHeightCm: heightCm as number,
    ...(weightKg === undefined ? {} : { sourceWeightKg: weightKg as number }),
    algorithmVersions: MEASUREMENT_ALGORITHM_VERSIONS,
  }
}

/**
 * `StorageError` also carries a `kind`, so the validation variant uses a tag
 * that cannot collide with any `StorageErrorKind`, keeping this a genuine
 * discriminated union.
 */
export interface MeasurementValidationError {
  readonly kind: 'measurementInvalid'
  readonly issues: readonly MeasurementIssue[]
}

export type SaveMeasurementError = MeasurementValidationError | StorageError

export async function saveMeasurementSession(
  db: SolaDatabase,
  input: MeasurementInput,
  now: Date,
): Promise<Result<MeasurementSessionRecord, SaveMeasurementError>> {
  const profile = await getProfile(db)
  if (!profile.ok) return err(profile.error)
  if (profile.value === undefined) {
    return err({
      kind: 'measurementInvalid',
      issues: [{ site: 'session', message: 'Add your height in setup before measuring.' }],
    })
  }

  const settings = await getSettings(db, now)
  if (!settings.ok) return err(settings.error)

  const summarised = summariseSites(input.sites, settings.value.circumferenceSpreadThresholdCm)
  if (!summarised.ok) return err({ kind: 'measurementInvalid', issues: summarised.error })

  // Everything the transaction needs is computed before it opens.
  const weightEntry = await getWeightEntry(db, input.date)
  const weightKg = weightEntry.ok ? weightEntry.value?.weightKg : undefined
  const phase = await getActivePhase(db)

  const timestamp = toIsoTimestamp(now)
  const record: MeasurementSessionRecord = {
    id: newId<'MeasurementSessionId'>(),
    date: input.date,
    sites: summarised.value,
    derived: deriveSnapshot(summarised.value, profile.value.heightCm, weightKg),
    ...(phase.ok && phase.value !== undefined ? { phaseId: phase.value.id } : {}),
    ...(input.conditionNote === undefined ? {} : { conditionNote: input.conditionNote }),
    recordedAt: timestamp,
    createdAt: timestamp,
    updatedAt: timestamp,
    schemaVersion: DB_SCHEMA_VERSION,
  }

  const written = await putMeasurementSession(db, record)
  if (!written.ok) return err(written.error)
  return ok(record)
}

export function isValidationIssues(
  error: SaveMeasurementError,
): error is MeasurementValidationError {
  return error.kind === 'measurementInvalid'
}

export function measurementErrorMessage(error: SaveMeasurementError): string {
  return isValidationIssues(error)
    ? error.issues.map((issue) => issue.message).join(' ')
    : error.message
}

export { storageError }
